import React, { useState, useEffect } from 'react';
import { Calendar, User, Phone, Clock, Search, Filter, Eye, CheckCircle, X, ArrowLeft, FileText, Download, Mail, Stethoscope } from 'lucide-react';
import { db } from './firebase';
import { collection, getDocs, updateDoc, doc, orderBy, query } from 'firebase/firestore';

const ViewAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [filteredAppointments, setFilteredAppointments] = useState([]);

  const statusOptions = ['pending', 'confirmed', 'completed', 'cancelled'];
  const serviceTypes = [
    'General Consultation',
    'Health Insurance Claim Support',
    'Wellness Program',
    'Corporate Health Plans',
    'Telemedicine',
    'Emergency Services'
  ];

  useEffect(() => {
    loadAppointments();
  }, []);

  useEffect(() => {
    let filtered = appointments.filter(appointment => {
      const matchesSearch = 
        (appointment.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (appointment.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (appointment.phone || '').includes(searchTerm) ||
        (appointment.message || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === '' || (appointment.status || 'pending') === statusFilter;
      const matchesService = serviceFilter === '' || (appointment.service || '') === serviceFilter;
      
      return matchesSearch && matchesStatus && matchesService;
    });
    
    setFilteredAppointments(filtered);
  }, [appointments, searchTerm, statusFilter, serviceFilter]);

  const loadAppointments = async () => {
    try {
      const q = query(collection(db, 'appointments'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const appointmentsData = [];
      querySnapshot.forEach((doc) => {
        appointmentsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setAppointments(appointmentsData);
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      await updateDoc(doc(db, 'appointments', appointmentId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      await loadAppointments();
      alert(`Appointment ${newStatus} successfully!`);
    } catch (error) {
      console.error('Error updating appointment:', error);
      alert('Error updating appointment. Please try again.');
    }
  };

  const exportToCSV = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Name,Email,Phone,Service,Date,Status,Message,Created Date\n"
      + filteredAppointments.map(appointment => 
          `"${appointment.name}","${appointment.email}","${appointment.phone}","${appointment.service}","${appointment.date}","${appointment.status}","${appointment.message.replace(/"/g, '""')}","${formatDate(appointment.createdAt)}"`
        ).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "appointments.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-700 border-gray-200';
    
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'confirmed': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getServiceIcon = (service) => {
    if (service.includes('Insurance') || service.includes('Claim')) return <FileText className="w-4 h-4" />;
    if (service.includes('Wellness')) return <CheckCircle className="w-4 h-4" />;
    if (service.includes('Corporate')) return <User className="w-4 h-4" />;
    return <Stethoscope className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-emerald-600 hover:text-emerald-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center">
                  <Calendar className="w-8 h-8 mr-3" />
                  Appointments Management
                </h1>
                <p className="text-purple-100 mt-2">View and manage all appointment requests</p>
              </div>
              <div className="bg-white/20 rounded-lg px-4 py-2">
                <span className="text-white font-semibold">{appointments.length} Appointments</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Filters and Controls */}
            <div className="mb-8 flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by name, email, phone, or message..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 flex-wrap">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">All Status</option>
                  {statusOptions.map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>

                <select
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">All Services</option>
                  {serviceTypes.map(service => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>

                <button
                  onClick={exportToCSV}
                  disabled={filteredAppointments.length === 0}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Export
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-yellow-50 rounded-2xl p-6 border border-yellow-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-600 text-sm font-medium">Pending</p>
                    <p className="text-3xl font-bold text-yellow-700">
                      {appointments.filter(a => (a.status || 'pending') === 'pending').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Confirmed</p>
                    <p className="text-3xl font-bold text-blue-700">
                      {appointments.filter(a => (a.status || 'pending') === 'confirmed').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-2xl p-6 border border-green-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-600 text-sm font-medium">Completed</p>
                    <p className="text-3xl font-bold text-green-700">
                      {appointments.filter(a => (a.status || 'pending') === 'completed').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-600 text-sm font-medium">Cancelled</p>
                    <p className="text-3xl font-bold text-red-700">
                      {appointments.filter(a => (a.status || 'pending') === 'cancelled').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                    <X className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Results Summary */}
            <div className="mb-6">
              <p className="text-gray-600">
                Showing {filteredAppointments.length} of {appointments.length} appointments
                {searchTerm && ` matching "${searchTerm}"`}
                {statusFilter && ` with status "${statusFilter}"`}
                {serviceFilter && ` for "${serviceFilter}"`}
              </p>
            </div>

            {/* Appointments List */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : filteredAppointments.length > 0 ? (
              <div className="space-y-6">
                {filteredAppointments.map((appointment) => (
                  <div key={appointment.id} className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center">
                            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mr-4">
                              <User className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-800">{appointment.name}</h3>
                              <p className="text-gray-600">{appointment.email}</p>
                              <p className="text-gray-600">{appointment.phone}</p>
                            </div>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(appointment.status || 'pending')}`}>
                            {(appointment.status || 'pending').charAt(0).toUpperCase() + (appointment.status || 'pending').slice(1)}
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="flex items-center text-gray-600">
                            {getServiceIcon(appointment.service)}
                            <span className="ml-2 font-medium">{appointment.service}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Preferred Date: {appointment.date}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Clock className="w-4 h-4 mr-2" />
                            <span>Submitted: {formatDate(appointment.createdAt)}</span>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <h4 className="font-semibold text-gray-800 mb-2">Message:</h4>
                          <p className="text-gray-600 leading-relaxed">{appointment.message}</p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 min-w-[200px]">
                        <h4 className="font-semibold text-gray-800 mb-2">Actions:</h4>
                        {(appointment.status || 'pending') === 'pending' && (
                          <button
                            onClick={() => updateAppointmentStatus(appointment.id, 'confirmed')}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Confirm
                          </button>
                        )}
                        {(appointment.status || 'pending') === 'confirmed' && (
                          <button
                            onClick={() => updateAppointmentStatus(appointment.id, 'completed')}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Complete
                          </button>
                        )}
                        {(appointment.status || 'pending') !== 'cancelled' && (appointment.status || 'pending') !== 'completed' && (
                          <button
                            onClick={() => updateAppointmentStatus(appointment.id, 'cancelled')}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </button>
                        )}
                        <button
                          onClick={() => window.location.href = `mailto:${appointment.email}?subject=Regarding your appointment&body=Dear ${appointment.name || 'Patient'},%0A%0A`}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-500 mb-2">
                  {searchTerm || statusFilter || serviceFilter ? 'No appointments found' : 'No appointments yet'}
                </h3>
                <p className="text-gray-400 mb-4">
                  {searchTerm || statusFilter || serviceFilter
                    ? 'Try adjusting your search criteria' 
                    : 'Appointment requests will appear here'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAppointments;
